import { useMemo } from "react"
import { useCartState } from "hooks/useCartV2"
import { useBookingV2 } from "context/BookingV2Context"
import { ITicketGroup, IDayType } from "types/improved-booking"
import { IPromotion } from "types/event"

// Use existing types from the codebase
type ProcessedTicketType = IDayType

// interface AvailableTicket {
//     _id: string
//     ar_description?: string
//     en_description?: string
//     en_title?: string
//     ar_title?: string
//     color?: string
//     discount?: {
//         current_value: number
//         previous_value: number
//     }
//     max?: number
//     min?: number
//     must_select_seat?: boolean
//     promotion?: IPromotion
//     price?: number
//     group?: ITicketGroup
//     wait_list?: number
// }

/*******************************************************
 * useTicketProcessing
 * -----------------------------------------------------
 * Produces a list of ticket types for the currently
 * selected timeslot/day with correctly merged data
 * (limit, wait_list, pricing, descriptions, etc.) using
 * the following source precedence (highest first):
 *   1. cartState.selectedDays.types
 *   2. event.time_slots[<selected>].types
 *   3. event.availableTickets (master tickets)
 *
 * It also exposes helpers to tell if a ticket is sold
 * out or in wait-list state. All code is strictly typed
 * – no `any` or `unknown`.
 *******************************************************/
export const useTicketProcessing = () => {
    const cartState = useCartState()
    const { event } = useBookingV2()
    // const availableTickets =
    //     (event?.availableTickets as AvailableTicket[]) || []

    // -------------------
    // Build processed list with correct precedence
    // -------------------
    // const selectedTimeslotId = cartState.selectedDays?.timeslot_id

    // const timeslotTypes: IDayType[] = useMemo(() => {
    //     if (!selectedTimeslotId || !Array.isArray(event?.time_slots)) return []
    //     const ts = (
    //         event!.time_slots as unknown as Array<{
    //             _id: string
    //             types: IDayType[]
    //         }>
    //     ).find((t) => t._id === selectedTimeslotId)
    //     return ts?.types ?? []
    // }, [selectedTimeslotId, event?.time_slots])

    const processedTicketTypes = useMemo(() => {
        const selectedDays = cartState?.selectedDays
        if (!selectedDays || !event) return []

        const selectedTimeslotId = selectedDays.timeslot_id
        const timeslot = event.time_slots?.find(
            (ts) => ts._id === selectedTimeslotId
        )
        const timeslotTypes = timeslot?.types ?? []

        // Map master tickets by _id
        const masterMap = new Map<string, any>()
        event.availableTickets?.forEach((t) => masterMap.set(t._id, t))

        // Merge quantities from timeslot with static data from master
        return timeslotTypes.map((type): ProcessedTicketType => {
            const id =
                typeof type.type_id === "string"
                    ? type.type_id
                    : type.type_id?._id || ""
            const master = masterMap.get(id) ?? {}
            return {
                _id: id,
                type_id: id,
                ar_description: master.ar_description ?? "",
                en_description: master.en_description ?? "",
                en_title: master.en_title ?? "Ticket",
                ar_title: master.ar_title ?? "تذكرة",
                color: master.color ?? "#ffffff",
                discount: master.discount ?? {
                    current_value: 0,
                    previous_value: 0,
                },
                limit: type.limit ?? 0,
                
                max: master.max ?? 0,
                
                min: master.min ?? 1,
                must_select_seat: master.must_select_seat ?? false,
                promotion: master.promotion,
                price: master.price ?? 0,
                group: master.group,
                
                //@ts-ignore
                wait_list: type.wait_list ?? 0,
                label: master.label,
            }
        })
    }, [
        cartState?.selectedDays,
        event,
        event?.time_slots,
        event?.availableTickets,
    ])

    // Helper functions for ticket status
    const isTicketSoldOut = (ticketType: ProcessedTicketType): boolean => {
        const isSoldOut =
            ticketType.limit === 0 || ticketType.limit < (ticketType.min || 1)
        const hasWaitList =
            typeof ticketType.wait_list === "number" && ticketType.wait_list > 0


        // A ticket is not sold out if it has wait_list available
        return isSoldOut && !hasWaitList
    }

    const isTicketWaitlistAvailable = (
        ticketType: ProcessedTicketType
    ): boolean => {
        const isSoldOut =
            ticketType.limit === 0 || ticketType.limit < (ticketType.min || 1)
        const hasWaitList =
            typeof ticketType.wait_list === "number" && ticketType.wait_list > 0

       

        return isSoldOut && hasWaitList
    }

    
    return {
        processedTicketTypes,
        isTicketSoldOut,
        isTicketWaitlistAvailable,
    }
}
